import { useQuery } from '@tanstack/react-query';
import { useAppClient } from '../data/ClientProvider';
import type { FieldMetadata } from '../data/client';
import { queryKeys } from '../data/queryKeys';

/**
 * Resolves what a relation should SAY: the target record's label-identifier
 * value. Both reads go through the canonical cache keys (INV-13), so a table
 * with fourteen rows pointing at five agencies makes five requests, not fourteen
 * - and a later batch endpoint changes this hook only.
 */
export function useRelationLabel(field: FieldMetadata | undefined, recordId: string | null) {
  const client = useAppClient();
  /**
   * `field` may be ABSENT, and that is a real state rather than a caller mistake: a
   * junction's endpoint is discovered through a second metadata read, so a row can
   * render before the field it labels has arrived. A hook cannot be called
   * conditionally, so the absence has to be expressible here - it resolves nothing,
   * exactly like a field with no relation settings.
   */
  const targetObjectId = field?.options?.relation?.targetObjectId;
  const enabled = targetObjectId !== undefined && recordId !== null && recordId.length > 0;

  const object = useQuery({
    queryKey: queryKeys.object(targetObjectId ?? 'none'),
    queryFn: () => client.metadata.getObject(targetObjectId ?? ''),
    enabled,
    staleTime: Number.POSITIVE_INFINITY,
  });

  const record = useQuery({
    queryKey: queryKeys.record(targetObjectId ?? 'none', recordId ?? 'none'),
    queryFn: () => client.records.getRecord(targetObjectId ?? '', recordId ?? ''),
    enabled,
  });

  const identifier = object.data?.labelIdentifierFieldId;
  const target = record.data;
  const value = identifier === undefined ? undefined : target?.values[identifier];

  /**
   * A RECORD WITH NO NAME SAYS "UNTITLED"; A RECORD THAT IS NOT THERE SAYS NOTHING
   * (audit P1-9's client half, left open by polish wave 3's ledger item 12).
   *
   * Wave 3 fixed the SERVER path: a nameless target resolves to "Untitled" in
   * `db/record-store/labels.ts`, so a table cell, a board card and a related row all say
   * that. This hook is the other path - a SINGLE-record read, which is what the record
   * page does - and it returned `undefined` for the same target, which `RelationDisplay`
   * turns into an em dash. So one nameless agency read "Untitled" in the table and
   * "no relation" on the record page: two honest answers that did not agree.
   *
   * BOTH CASES ARE DECIDED HERE, which is what wave 3 said whoever took this had to do:
   *
   *  - the record was READ and has no label-identifier value, or its object has no
   *    label-identifier field at all -> **"Untitled"**, the word `recordTitle`, the record
   *    page's own heading, the create dialog and the server all already use;
   *  - the record was NOT FOUND, or the read was refused -> **`undefined`**, which
   *    `RelationDisplay` renders as `EmptyValue`. This is deliberately NOT the server's
   *    answer, which keeps the id: the server's consumer is a machine boundary that has
   *    nothing else to hand back, and a CELL is not - `RelationDisplay`'s own docblock
   *    forbids a uuid in one outright ("an id in a cell is noise a user cannot act on").
   *    A phrase for the missing case would be new copy on a path nobody has reported, and
   *    it would have to stay distinguishable from "Untitled" on every surface.
   *
   * `undefined` therefore means exactly one thing to a caller: there is nothing to show.
   * `isPending` is what separates that from "not known yet".
   */
  const label =
    target === undefined || target === null
      ? undefined
      : typeof value === 'string' && value.length > 0
        ? value
        : 'Untitled';

  return {
    label,
    objectLabel: object.data?.labelSingular,
    isPending: enabled && (object.isPending || record.isPending),
  };
}
